import React, { useMemo, useRef, useState } from 'react';
import { Button, Col, Row } from 'antd';
import { connect } from 'react-redux';
import './index.less';
import {
	getSupplierPrivateNetworksForLocationForBuyer,
	getSupplierPrivateNetworksForLocationForSupplier,
	supplierPrivateNetworksV2ForBuyer,
	supplierPrivateNetworksV2ForSupplier,
	updateRankForSuppliersForBuyer,
	updateRankForSuppliersForSupplier,
} from '../../thunks/supplier_private_networks_thunks';

import {
	problemTypesHeirarchicalForBuyer,
	problemTypesHeirarchicalForSupplier,
} from '../../thunks/problem_types_thunks';

import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
	updatePrivateNetworkLocationsPageFilters,
	clearAndUpdatePrivateNetworkLocationsPageFilters,
	PRIVATE_NETWORK_LOCATIONS_PAGE_FILTERS,
} from '../../thunks/locations_thunks';

import LocationList from './LocationList';
import ProblemTypesList from './ProblemTypesList';
import PrivateNetworkLocationsFilterBar from './PrivateNetworkLocationsFilterBar';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';
import ProblemTypeInlineFilter from '../inline_tree_filter/ProblemTypeInlineFilter';
import LocationsInlineFilter from '../inline_tree_filter/LocationsInlineFilter';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { FILTER_CONFIG_NAMES, ROLE_TYPES } from '../../utils/DataConstants';
import {
	changeFilterValueToArrayOfIds,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { withRouter } from 'react-router';
import useMountEffect from '../../hooks/useMountEffect';
const queryString = require('qs');

const FILTER_DROPDOWN_COLLECTION = 'workOrdersAdvancedFilters';

export const PrivateNetworkLocationsPage = ({
	problemTypes,
	fetchSupplierPrivateNetworksForLocation,
	fetchProblemTypes,
	fetchSupplierPrivateNetworkV2,
	fetchLocations,
	locations,
	updateRank,
	pageFilters,
	updateFilters,
	clearFilters,
}) => {
	const [selected, setSelected] = useState({ id: null, name: null });

	const urlFiltersRef = useRef<any>(null);
	const hasSeenReduxFiltersRef = useRef(false);
	if (urlFiltersRef.current === null) {
		const parsed = queryString.parse(window.location.search, { ignoreQueryPrefix: true });
		const initial: any = {};
		if (parsed.problemTypeIds) initial.problemTypeIds = parsed.problemTypeIds;
		if (parsed.locationIds) initial.locationIds = parsed.locationIds;
		urlFiltersRef.current = initial;
	}
	if (Object.keys(pageFilters).length > 0) {
		hasSeenReduxFiltersRef.current = true;
	}

	useMountEffect(() => {
		if (Object.keys(urlFiltersRef.current).length > 0) {
			updateFilters(urlFiltersRef.current);
		}
	});

	const effectiveFilters =
		!hasSeenReduxFiltersRef.current && Object.keys(urlFiltersRef.current).length > 0
			? { ...pageFilters, ...urlFiltersRef.current }
			: pageFilters;

	const problemTypeIds = useMemo(
		() => changeFilterValueToArrayOfIds(nullSafeGet('problemTypeIds', effectiveFilters)),
		[effectiveFilters]
	);
	const locationIds = useMemo(
		() => changeFilterValueToArrayOfIds(nullSafeGet('locationIds', effectiveFilters)),
		[effectiveFilters]
	);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Problem Types',
				fieldName: FILTER_CONFIG_NAMES.PROBLEM_TYPE,
				placeholderText: 'Search for a problem type',
				stateSlice: problemTypes,
				targetCollectionName: FILTER_DROPDOWN_COLLECTION,
				component: ProblemTypeInlineFilter,
				mode: 'multiple',
				fetch: fetchProblemTypes,
				keyAccessor: (item) => item.id,
				valueAccessor: (item) => item.id,
				renderItem: (item) => item.hierarchyName || item.name,
				labelAccessor: (item) => item.hierarchyName || item.name,
				filtersValueAccessor: (slice) =>
					changeFilterValueToArrayOfIds(
						nullSafeGet(`${PRIVATE_NETWORK_LOCATIONS_PAGE_FILTERS}.filters.problemTypeIds`, slice)
					),
				handleChange: (ids) =>
					updateFilters({
						problemTypeIds: ids && ids.length > 0 ? ids.join(',') : undefined,
					}),
			},
			{
				label: 'Locations',
				fieldName: FILTER_CONFIG_NAMES.LOCATION,
				placeholderText: 'Search for locations',
				stateSlice: locations,
				targetCollectionName: FILTER_DROPDOWN_COLLECTION,
				component: LocationsInlineFilter,
				mode: 'multiple',
				fetch: fetchLocations,
				keyAccessor: (item) => item.id,
				valueAccessor: (item) => item.id,
				renderItem: (item) => item.name,
				labelAccessor: (item) => item.name,
				filtersValueAccessor: (slice) =>
					changeFilterValueToArrayOfIds(
						nullSafeGet(`${PRIVATE_NETWORK_LOCATIONS_PAGE_FILTERS}.filters.locationIds`, slice)
					),
				handleChange: (ids) =>
					updateFilters({
						locationIds: ids && ids.length > 0 ? ids.join(',') : undefined,
					}),
			},
		],
		[problemTypes, locations, fetchProblemTypes, fetchLocations, updateFilters]
	);

	const hasActiveFilter = problemTypeIds.length > 0 || locationIds.length > 0;

	return (
		<div className="px-4">
			<div className="flex flex-col gap-3 pb-4 pt-4">
				<div className="flex flex-row items-center gap-2">
					<PrivateNetworkLocationsFilterBar
						filterConfig={filterConfig}
						filtersStateSlice={locations}
					/>
					{hasActiveFilter ? (
						<Button size="large" onClick={clearFilters}>
							Clear Filters
						</Button>
					) : null}
				</div>
				{hasActiveFilter ? (
					<div className="flex w-full flex-row flex-wrap items-center gap-2">
						<SelectedFilters
							updateFilters={updateFilters}
							filterValues={pageFilters}
							filterConfig={filterConfig}
							pillsClosable
						/>
					</div>
				) : null}
			</div>
			<Row>
				<Col span={8}>
					<div
						style={{ borderRight: 'solid 1px rgb(229 231 235)' }}
						className="h-full  border-r border-owgray bg-white"
					>
						<div className="h-full p-6">
							<LocationList
								fetchLocations={fetchLocations}
								setSelected={setSelected}
								selected={selected}
								locations={getRecordsForTargetCollection(locations, 'rankingPageLocations')}
								total={locations['rankingPageLocations'].pagination.total}
								problemTypeIds={problemTypeIds}
								locationIds={locationIds}
							/>
						</div>
					</div>
				</Col>
				<Col span={16}>
					<div className="block h-full w-full rounded border-2 ">
						<ProblemTypesList
							updateRank={updateRank}
							selected={selected}
							fetchSupplierPrivateNetworksForLocation={fetchSupplierPrivateNetworksForLocation}
							fetchSupplierPrivateNetworkV2={fetchSupplierPrivateNetworkV2}
							problemTypeIds={problemTypeIds}
						/>
					</div>
				</Col>
			</Row>
		</div>
	);
};

const mapStateToProps = (state) => ({
	problemTypes: state.problem_types,
	locations: state.locations,
	userType: (state as any).session.userType,
	pageFilters: nullSafeGetOrElse(
		`locations.${PRIVATE_NETWORK_LOCATIONS_PAGE_FILTERS}.filters`,
		state,
		{}
	),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchLocations: (params, targetCollectionName, pagination, sorting, filters, addTo) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readLite(
						params,
						targetCollectionName || 'rankingPageLocations',
						pagination,
						sorting,
						filters,
						addTo
				  )
				: locationsRestCrudThunksForBuyer.readLite(
						params,
						targetCollectionName || 'rankingPageLocations',
						pagination,
						sorting,
						filters,
						addTo
				  )
		),
	fetchSupplierPrivateNetworksForLocation: (locationId, params) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? getSupplierPrivateNetworksForLocationForSupplier(locationId, params)
				: getSupplierPrivateNetworksForLocationForBuyer(locationId, params)
		),
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesHeirarchicalForSupplier(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: problemTypesHeirarchicalForBuyer(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchSupplierPrivateNetworkV2: (
		additionalParams,
		targetCollectionName,
		pagination,
		sorting,
		filters
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierPrivateNetworksV2ForSupplier(
						additionalParams,
						targetCollectionName,
						pagination,
						sorting,
						filters
				  )
				: supplierPrivateNetworksV2ForBuyer(
						additionalParams,
						targetCollectionName,
						pagination,
						sorting,
						filters
				  )
		),
	updateRank: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? updateRankForSuppliersForSupplier(entity)
				: updateRankForSuppliersForBuyer(entity)
		),
	updateFilters: (patch) =>
		dispatch(updatePrivateNetworkLocationsPageFilters(ownProps.history, patch)),
	clearFilters: () => dispatch(clearAndUpdatePrivateNetworkLocationsPageFilters(ownProps.history)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(PrivateNetworkLocationsPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
